import {Strings} from '@/assets';
import BankStatementParser from './BankStatementParser';
import MetaParser from './MetaParser';
import Country from './Country';

class VisitorParser extends MetaParser implements Visitor {
  aadhar = false;
  aadhar_details: AadharDetails | null = null;
  aadhar_verified = false;
  address = '';
  bank_detail_owner = '';
  bank_statements: BankStatement[] = [];
  bank_statement_verified = false;
  code = '';
  country_code = '';
  date_of_birth = '';
  email = '';
  face_liveness = null;
  father_name = '';
  fever_certificate = null;
  first_name = '';
  first_time_visit = false;
  flight_details: FlightDetails[] | null = [];
  gender = '';
  income_tax_return: IncomeTaxReturn | null = null;
  itr_exempted_address_documents: ItrExemptedAddressDocuments[] | null = [];
  itr_exempted_state = '';
  itr_linked = false;
  itr_linked_method = '';
  internal_agent_initiated = null;
  last_name = '';
  occupation = null;
  passport: Passport | null = null;
  photograph: Photograph | null = null;
  phone_number = '';
  primary = true;
  relation_with_applicant = null;
  supporting_documents: SponsorDocument[] = [];
  travel_detail = null;
  visitor_id = '';
  visas: Visa[] = [];
  comment = '';
  bank_statement_consent!: boolean;
  self_sponsored!: boolean;
  application_validations: ApplicationValidation[] = [];
  itr_details: ItrDetails[] = [];
  bsa_id = '';
  finbox_session_id = '';
  _bank_sponsor_documents: SponsorDocument[] = [];
  full_name = '';
  itr_name_matched = false;
  aadhar_matched = false;
  bank_statement_count = 0;

  constructor(visitor: Visitor) {
    super();
    this.util.assign(this, visitor);
    this.self_sponsored = visitor?.self_sponsored;
    this.bank_statement_consent = visitor?.bank_statement_consent;
    this._bank_sponsor_documents = this.parseDocuments(
      visitor?.bank_sponsor_documents || [],
    ).filter(i => !!i.document_type);
  }

  // validations

  get passport_valid() {
    return this.passport?.obj_id && this.email && this.phone_number;
  }

  parseDocuments(documents: SponsorDocument[]) {
    const passport_front = documents.find(
      i => i.document_type === 'passport_front',
    );
    const passport_back = documents.find(
      i => i.document_type === 'passport_back',
    );
    const remaining = documents.filter(
      i =>
        i.document_type !== 'passport_front' &&
        i.document_type !== 'passport_back',
    );
    const hasFrontOrBack = !!passport_front || !!passport_back;

    const passport = {} as Record<string, unknown>;

    Object.keys(passport_front || {}).forEach(key => {
      const value = passport_front?.[key as keyof typeof passport_front];
      if (value) {
        passport[key] = value;
      }
    });

    Object.keys(passport_back || {}).forEach(key => {
      const value = passport_back?.[key as keyof typeof passport_back];
      if (value) {
        passport[key] = value;
      }
    });

    return [
      ...remaining,
      {
        ...passport,
        has_front: !!passport_front,
        has_back: !!passport_back,
        document_type: hasFrontOrBack ? 'passport' : '',
        passport_front: passport_front?.url || '',
        passport_back: passport_back?.url || '',
        passport_front_id: passport_front?.obj_id || '',
        passport_back_id: passport_back?.obj_id || '',
        passport_front_number: passport_front?.number || '',
        passport_back_number: passport_back?.number || '',
      },
    ] as SponsorDocument[];
  }

  get visa_valid() {
    const isNull = this.first_time_visit === null;
    if (isNull) {
      return false;
    } else {
      return this.first_time_visit ? true : this.visas.length > 0;
    }
  }

  get applicant_name() {
    return this.id ? 'Applicant name: ' + this.visitor_full_name : '';
  }

  get bank_sponsor_documents() {
    return this._bank_sponsor_documents;
  }

  set bank_sponsor_documents(arg: SponsorDocument[] | null) {
    arg;
  }

  get sponsor_documents() {
    return (
      this.bank_sponsor_documents?.map(i => ({
        ...i,
        src: this.parseImage(i.url),
      })) || []
    );
  }

  get visitor_aadhar(): DetailsCard | null {
    if (!this.aadhar_linked) {
      return null;
    }
    return {
      date_of_birth: this.aadhar_details?.date_of_birth || '',
      full_name: this.aadhar_details?.full_name || '',
      address: this.aadhar_details?.formatted_address || '',
      gender: this.aadhar_details?.gender_detail || '',
    };
  }

  get sponsor() {
    const type = this.sponsor_relation_document?.document_type;
    const proof = Strings.RELATION_PROOFS.find(i => i.value === type);
    const doc = this.sponsor_relation_document;
    return {
      relation: proof?.label || '',
      document:
        proof?.value === 'gst_certificate'
          ? true
          : this.parseImage(this.sponsor_relation_document?.url),
      type,
      is_manual: proof?.verification_type === 'manual' && !!doc?.gender,
      is_document: proof?.verification_type === 'document' && !doc?.gender,
      is_passport:
        doc?.document_type === 'passport' && doc.has_back && doc.has_front,
      ...doc,
    };
  }

  get financial_valid() {
    return this.itr_linked && this.bank_statements.length > 0;
  }

  get is_self_sponsored() {
    return this.self_sponsored === null
      ? this.old_self_sponsored
      : this.self_sponsored;
  }

  get id() {
    return `visitor${this.visitor_id}`;
  }

  get force_self_sponsored() {
    return this.getForceSelfSponsored();
  }

  get force_other_sponsored() {
    return this.getForceOtherSponsored();
  }

  get has_statements() {
    return this.bank_statements.length > 0;
  }

  get has_validations() {
    return this.application_validations.length > 0;
  }

  get statement_matched() {
    return this.application_validations.every(i => i.matched);
  }

  get photo_id() {
    return this.photograph?.photograph_id;
  }

  get photo_url() {
    return this.parseImage(this.photograph?.url);
  }

  get number() {
    return this.country_code
      ? '+' + this.country_code + this.phone_number
      : this.phone_number;
  }

  get aadhar_linked() {
    return !!this.aadhar_verified;
  }

  get statement() {
    return new BankStatementParser(this.bank_statements || [], 165);
  }

  get statement_valid_months() {
    return new BankStatementParser(this.bank_statements || [], 180)
      .valid_months;
  }

  get show_comment() {
    return this.ask_for_additional_comment && !!this.comment;
  }

  get visitor_full_name() {
    return this.join([this.first_name, this.last_name]);
  }

  get old_self_sponsored() {
    return this.self_statements.length > 0
      ? true
      : this.sponsor_relation_document?.supporting_document_id
        ? false
        : undefined;
  }

  get also_sponsor() {
    if (this.is_south_korea) {
      return this.sponsor_validations.length > 0;
    } else {
      return this.self_statements.length > 0 &&
        (this.sponsor_statements.length > 0 ||
          !!this.sponsor_relation_document?.obj_id)
        ? true
        : undefined;
    }
  }

  get qualified_visa() {
    return this.filterVisa('prequalified_countries');
  }

  get other_visa() {
    return this.filterVisa('other');
  }

  get additional_documents() {
    return this.supporting_documents.map(i => ({
      ...i,
      src: this.parseImage(i.url),
    }));
  }

  private filterVisa(key: string) {
    return this.visas
      .filter(v => v.visa_category === key)
      .map(v => ({
        ...v,
        src: this.parseImage(v.url || v.raw_front_image || ''),
      }));
  }

  get pan_number() {
    return this.itr_details[0]?.pan_no || '';
  }

  get show_visas() {
    return this.ask_for_visa;
  }

  get first_visit() {
    return this.visas.length > 0 ? false : this.first_time_visit;
  }

  get sponsor_checked() {
    return !!this.sponsor_relation_document?.obj_id;
  }

  get sponsor_relation_document() {
    return this.bank_sponsor_documents?.find(
      i => i.image_present || !!i.gender,
    );
  }

  get sponsor_verified_document() {
    return this.bank_sponsor_documents?.find(i =>
      i.document_type === 'passport' ? i.has_back || i.has_front : !!i.gender,
    );
  }

  get sponsor_gst_details() {
    return this.bank_sponsor_documents
      ?.reverse()
      ?.find(i => i.document_type === 'gst_certificate' && i.full_name);
  }

  get is_itr_exempted() {
    return this.itr_linked_method === 'exemption';
  }

  get is_itr_not_submitted() {
    return Country.select({
      southkorea: this.itr_linked_method === 'not_submitted',
      default: false,
    });
  }

  get show_itr_details() {
    return !this.is_itr_exempted && !this.is_itr_not_submitted;
  }

  get itr_address_proof() {
    return this.itr_exempted_address_documents?.find(i => i.image_present);
  }

  getEmbassyStatement = (validation: ApplicationValidation, key: string) => {
    const embassyStatement = () => {
      if ('statement_ids' in validation) {
        const statements = this.bank_statements?.filter(statement =>
          validation.statement_ids?.includes(statement.bank_statement_id),
        );
        if (statements.length > 0) {
          const start_dates = [];
          const end_dates = [];
          for (let i = 0; i < statements.length; i++) {
            start_dates.push(statements[i].statement_start_date);
            end_dates.push(statements[i].statement_end_date);
          }
          const [statement] = statements;
          const lowestStartDate = start_dates.sort()[0];
          const highestEndDate = end_dates.sort()[end_dates.length - 1];
          return {
            bank_name: statement.bank_name,
            account_name: statement.account_name,
            account_number: statement.account_number,
            statement_start_date: lowestStartDate,
            statement_end_date: highestEndDate,
          };
        }
      } else {
        return {};
      }
    };
    return embassyStatement()?.[key as keyof EmbassyStatement] || '';
  };

  get sponsor_passport() {
    return this.sponsor_verified_document;
  }

  get sponsor_document_linked() {
    const doc = this.sponsor_verified_document;
    const isPassport = doc?.document_type === 'passport';
    return isPassport
      ? !!doc.passport_back && !!doc.passport_front
      : !!doc?.gender;
  }

  get show_sponsor_details() {
    return this.sponsor.is_manual && !!this.sponsor_verified_document;
  }

  get sponsor_details_type() {
    const doc = {
      aadhar_card: 'Aadhar',
      itr: 'ITR',
      passport: 'Passport',
    }[this.sponsor.type!];
    return {
      title: `Sponsor ${doc} Details`,
      mini_title: doc,
    };
  }

  get is_gst_certificate() {
    return this.sponsor_relation_document?.document_type === 'gst_certificate';
  }

  get self_statements() {
    const filteredStatements = this.bank_statements.filter(this.isSelf);
    if (filteredStatements.length > 0) {
      return filteredStatements;
    }

    if (this.internal_agent_initiated) {
      if (typeof this.self_sponsored === 'boolean') {
        return this.self_sponsored ? this.bank_statements : [];
      }
    }

    return [];
  }

  get self_validations() {
    return this.application_validations.filter(this.isSelf);
  }

  get sponsor_validations() {
    return this.application_validations.filter(this.isSponsor);
  }

  get sponsor_check_disabled() {
    return (
      this.bank_statements.length > 0 ||
      !!this.sponsor_relation_document?.obj_id
    );
  }

  get sponsor_statements() {
    const filteredStatements = this.bank_statements.filter(this.isSponsor);
    if (filteredStatements.length > 0) {
      return filteredStatements;
    }

    if (this.internal_agent_initiated) {
      if (typeof this.self_sponsored === 'boolean') {
        return this.self_sponsored ? [] : this.bank_statements;
      }
    }

    return [];
  }

  private getForceSelfSponsored() {
    if (this.is_south_korea) {
      return false;
    } else {
      if (!this.has_statements) return false;
      const self =
        this.bank_statements.some(this.isSelf) && !this.is_gst_certificate;
      return self && !this.self_sponsored;
    }
  }

  private getForceOtherSponsored() {
    if (this.is_south_korea) {
      return false;
    } else {
      if (!this.has_statements) return false;
      const sponsor = this.bank_statements.every(this.isSponsor);
      return sponsor && this.self_sponsored;
    }
  }
}

export default VisitorParser;
